import abTestManager from '@zillow/home-details-abtest-manager';
import { event } from '@zillow/universal-analytics';
import { getPropertyInfo } from '@zillow/property-info-store';

const SCORE_THRESHOLD = 40;
const GALLERY_AD_DISPLAY = true;
const GALLERY_AD_NO_DISPLAY = false;

const TriggerType = {
    IMPRESSION: 'impression',
    INTERACTION: 'interaction',
};

export function isAllowedPageVariant(listtp) {
    const allowedVariant = { buy_agent: 'fsba' }; // update the field when you need to support different variants
    let allowedKey;
    let key;
    for (key in allowedVariant) {
        if (allowedVariant[key]) {
            if (key === listtp) {
                allowedKey = allowedVariant[key];
                return allowedKey;
            }
        }
    }
    return allowedKey;
}

export function getSlotLayoutValue(isFullWidth, isMobileDevice) {
    if (isMobileDevice) {
        return 'square';
    }
    if (isFullWidth) {
        return 'rectangle';
    }
    return 'square';
}

export function getScoreTresholdStatus(scores = []) {
    if (scores?.length) {
        const likelyBuyer = scores.filter(s => s.modelType === 'likely_buyer')[0];
        const lbScore = likelyBuyer?.scorePrecentile;
        const isScorethresholdMet = SCORE_THRESHOLD >= lbScore;
        const scoreList = appendPersonaScores(scores);
        const personaScores = { lbScore, scoreList };
        return { isScorethresholdMet, personaScores };
    }

    return { isScorethresholdMet: false, personaScores: {} };
}

export function getRestrictionStatus(listtp) {
    const supportedPage = isAllowedPageVariant(listtp);
    return {
        supportedPage,
    };
}

export function isNoAdNoCtaEnabled(isFSBA, isLoggedIn) {
    const noAdNoCta = abTestManager.isTreatment(
        'HDP_MEDIASOLUTIONS_ADS_WEB_PHOTOGALLERY_FSBA_GUID',
        'NO_AD_NO_CTA'
    );
    return noAdNoCta && isFSBA && !isLoggedIn?.current;
}

export function appendPersonaScores(scores) {
    const appendendList = [];
    scores.forEach(s => {
        const obfuscatedList = s.modelType
            .split('_')
            .map(i => i.charAt(0))
            .join('');
        appendendList.push(`${obfuscatedList}_${s.scorePrecentile}`);
    });
    const personaScores = appendendList.join(',');
    return personaScores;
}

export function isAdAllowed(persona) {
    const { isScorethresholdMet, personaScores } = getScoreTresholdStatus(persona);
    if (isScorethresholdMet) {
        return { adAllowed: GALLERY_AD_DISPLAY, personaScores };
    }
    return { adAllowed: GALLERY_AD_NO_DISPLAY, personaScores: {} };
}

export const getToggleState = state => state?.toggleActionBar?.isActionBarViewAbleOnAdTile || false;

export const getClassname = (isFullWidth, isHollywood) => {
    const classNameStyle =
        isFullWidth && !isHollywood
            ? 'media-stream-tile media-stream-tile--fullwidth media-stream-tile--halfheight media-stream-tile--upsell'
            : 'media-stream-tile';
    const abrStyle =
        isFullWidth && !isHollywood ? 'media-stream-tile--fullwidth' : 'media-stream-tile';
    return { classNameStyle, abrStyle };
};

export function triggerAdSeen(ctxBlock = {}, adClick) {
    const adStreamEventObj = {
        envelope: {
            event_template_id: '4',
            event_template_version_id: '1',
            event_type_id: '3536',
            event_type_version_id: '3',
            event_client_start_dtm: new Date().toISOString(),
        },
        clickstream_trigger: {
            trigger_location_nm: 'home_details|photo',
            trigger_type_nm: TriggerType.IMPRESSION,
            trigger_object_nm: 'media_lightbox_component|photos_viewer',
            trigger_source_nm: 'photo_carousel|bottom',
        },
        semantic: {
            semantic_event_nm: 'view_content',
            topic_tag_txt: ['exposure'],
        },
        ...ctxBlock,
    };

    const adStreamEventClickObj = {
        envelope: {
            event_template_id: '243',
            event_template_version_id: '1',
            event_type_id: '2135',
            event_type_version_id: '3',
            event_client_start_dtm: new Date().toISOString(),
        },
        clickstream_trigger: {
            trigger_location_nm: 'mst_location',
            trigger_type_nm: TriggerType.INTERACTION,
            trigger_object_nm: 'no_trigger_object',
            trigger_source_nm: 'mst_source',
        },
        semantic: {
            semantic_event_nm: 'click_through_to_page',
            topic_tag_txt: [''],
        },
        ...ctxBlock,
    };

    const shopperAdStream = adClick ? adStreamEventClickObj : adStreamEventObj;
    const propInfo = adClick ? {} : { property_info: getPropertyInfo() };
    const newLaneEvent = {
        ...shopperAdStream,
        ...propInfo,
    };
    return { newLaneEvent };
}

export function sendNewLaneEvent(scores, viewer) {
    if (viewer) {
        const { persona } = viewer;
        const scoreList = persona?.ccdPropensityScores || [];
        const { personaScores } = getScoreTresholdStatus(scoreList);
        const { lbScore } = personaScores;
        const ctxBlock =
            Object.keys(viewer).length > 0
                ? { user_info: { lb_percentile_nb: lbScore || '' } }
                : { user_info: { lbs_percentile_nb: lbScore || '' } };
        event(triggerAdSeen(ctxBlock));
    } else {
        const ctxBlock = { user_info: { lbs_percentile_nb: scores?.lbScore || '' } };
        event(triggerAdSeen(ctxBlock));
    }
}

export function sendAdClickEvent() {
    window.addEventListener('message', e => {
        if (e.data.eventAction === 'LearnMoreCTA') {
            event(triggerAdSeen({}, true));
        }
    });
}

export function sendViewEvent(ctxBlock = {}) {
    const adViewEventObj = {
        envelope: {
            event_template_id: '284',
            event_template_version_id: '1',
            event_type_id: '4784',
            event_type_version_id: '3',
            event_client_start_dtm: new Date().toISOString(),
        },
        clickstream_trigger: {
            trigger_location_nm: 'mst_location',
            trigger_type_nm: TriggerType.IMPRESSION,
            trigger_object_nm: 'no_trigger_object',
            trigger_source_nm: 'mst_source',
        },
        semantic: {
            semantic_event_nm: 'view_content',
            topic_tag_txt: [''],
        },
    };
    const propInfo = { property_info: getPropertyInfo(), mst_info: ctxBlock };
    const newLaneEvent = {
        ...adViewEventObj,
        ...propInfo,
    };
    event(newLaneEvent);
}
